import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { toast } from 'sonner'
import {
  Search,
  Wrench,
  FileText,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Quote,
  Code,
  Link2,
  Sparkles,
} from 'lucide-react'
import { AgentVisualEditor, AgentVisualIcon } from '@/components/agents/AgentVisualEditor'
import { AgentTimezonePicker } from '@/components/agents/AgentTimezonePicker'
import { AgentDocumentsSelector } from '@/components/agents/AgentDocumentsSelector'
import { PromptRichEditor } from '@/components/agents/PromptRichEditor'
import {
  buildSelectedToolDynamicKeys,
  buildSystemMentionSuggestions,
  buildToolMentionSuggestions,
  findDuplicateSelectedToolDynamicKeys,
  findUnknownReferencedDynamicTokens,
} from '@/components/agents/promptMentionsShared'
import { cn } from '@/lib/utils'
import { getToolVisibility, herramientaBloqueadaPorAudiencia } from '@/lib/toolVisibility'
import { toolsService } from '@/services/toolsService'
import { tagService, type Tag } from '@/services/TagService'
import { agentsService } from '@/services/agentsService'
import { loadAgentVisualsMap, saveAgentVisual, type StoredAgentVisual } from '@/lib/agent-visuals'
import { DEFAULT_AGENT_TIMEZONE } from '@/lib/timezones'
import {
  openaiReasoningEffortForApiPayload,
  parseModelId,
  toModelId,
} from '@/lib/agent-models'
import { getFirstCatalogModelId, isModelIdInCatalog } from '@/lib/agent-model-ui'
import { loadToolIconsMap, type StoredToolIcon } from '@/lib/tool-icons'
import { log } from '@/lib/logger'
import type {
  Agent,
  AgentAudience,
  AgentCreatePayload,
  AgentUpdatePayload,
  AgentAvailableDocument,
  AgentAvailableModel,
  AgentAvailableModelsResponse,
  AgentDocument,
  DocumentPermission,
} from '@/types/agent'
import type { MCPAction } from '@/types/tool'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { AgentCapabilities } from '@/components/agents/AgentCapabilities'
import { saveCapabilities } from '@/components/agents/save-capabilities'

function normalizeId(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

interface AgentFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  agent?: Agent | null
  defaultToolIds?: string[]
  onSuccess?: () => void
}

type AgentFormState = {
  name: string
  display_name: string
  description: string
  is_enabled: boolean
  timezone: string
  base_prompt: string
  custom_instructions: string
  tool_ids: string[]
  document_ids: number[]
  audience: AgentAudience
}

type PromptSelection = {
  start: number
  end: number
}

const DEFAULT_BASE_PROMPT =
  'Eres un asistente especializado. Responde de forma clara y usa herramientas cuando aporten valor.'
const EMPTY_TOOL_IDS: string[] = []

function toErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'body' in error) {
    const body = (error as { body?: unknown }).body
    if (body && typeof body === 'object') {
      const entries = Object.entries(body as Record<string, unknown>)
      if (entries.length > 0) {
        const [field, value] = entries[0]
        if (Array.isArray(value) && value[0]) {
          return `${field}: ${String(value[0])}`
        }
        return `${field}: ${String(value)}`
      }
    }
  }
  if (error instanceof Error && error.message) return error.message
  return 'Ha ocurrido un error inesperado.'
}

function mergePromptParts(basePrompt: string, customInstructions: string): string {
  return [basePrompt.trim(), customInstructions.trim()].filter(Boolean).join('\n\n')
}

export function AgentForm({ open, onOpenChange, agent, defaultToolIds, onSuccess }: AgentFormProps) {
  const { t } = useTranslation()
  const isEditMode = Boolean(agent)
  const initialToolIds = defaultToolIds ?? EMPTY_TOOL_IDS
  const [loading, setLoading] = React.useState(false)
  const [toolsLoading, setToolsLoading] = React.useState(false)
  const [tools, setTools] = React.useState<MCPAction[]>([])
  const [availableTags, setAvailableTags] = React.useState<Tag[]>([])
  const [toolIcons, setToolIcons] = React.useState<Record<string, StoredToolIcon>>({})
  const [toolQuery, setToolQuery] = React.useState('')
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [visual, setVisual] = React.useState<StoredAgentVisual>({})
  const [coverPrompt, setCoverPrompt] = React.useState('')
  const [coverStyle, setCoverStyle] = React.useState('cinematic')
  const [coverGenerating, setCoverGenerating] = React.useState(false)
  const [coverError, setCoverError] = React.useState<string | null>(null)
  const [selectedModel, setSelectedModel] = React.useState(() => toModelId(agent?.llm_provider, agent?.llm_model))
  const [availableModelOptions, setAvailableModelOptions] = React.useState<AgentAvailableModel[]>([])
  const [mediaProcessingModel, setMediaProcessingModel] = React.useState<AgentAvailableModel | null>(null)
  const [loadingAvailableModels, setLoadingAvailableModels] = React.useState(false)
  const [availableModelsError, setAvailableModelsError] = React.useState<string | null>(null)
  const [formData, setFormData] = React.useState<AgentFormState>({
    name: '',
    display_name: '',
    description: '',
    is_enabled: true,
    timezone: DEFAULT_AGENT_TIMEZONE,
    base_prompt: DEFAULT_BASE_PROMPT,
    custom_instructions: '',
    tool_ids: [],
    document_ids: [],
    audience: 'internal',
  })
  const [documentsOpen, setDocumentsOpen] = React.useState(true)
  // «Qué puede hacer» (pantallas-abilities-y-skills): what it has now, and what
  // was saved, so only what changed is sent.
  const [capabilitiesOpen, setCapabilitiesOpen] = React.useState(false)
  const [capabilities, setCapabilities] = React.useState<{ abilityIds: string[]; skillIds: string[] }>({ abilityIds: [], skillIds: [] })
  const [savedCapabilities, setSavedCapabilities] = React.useState<{ abilityIds: string[]; skillIds: string[] }>({ abilityIds: [], skillIds: [] })
  const [toolsOpen, setToolsOpen] = React.useState(true)
  const [promptEditorOpen, setPromptEditorOpen] = React.useState(false)
  const [promptEditorMode, setPromptEditorMode] = React.useState<'markdown' | 'preview'>('markdown')
  const [documentCatalog, setDocumentCatalog] = React.useState<Record<number, AgentDocument>>({})
  // What the agent may do with each document, and what it had when the form
  // opened: the difference is what saving changes (permisos-sobre-knowledge, 7.1, 7.3).
  const [permissions, setPermissions] = React.useState<Record<number, DocumentPermission>>({})
  const [savedPermissions, setSavedPermissions] = React.useState<Record<number, DocumentPermission>>({})
  const promptEditorTextareaRef = React.useRef<HTMLTextAreaElement | null>(null)
  const promptPreviewRef = React.useRef<HTMLDivElement | null>(null)
  const promptSelectionRef = React.useRef<PromptSelection | null>(null)
  const promptPreviewSelectionTextRef = React.useRef<string>('')

  /** Prefer explicit selection; in edit mode fall back to saved agent ids if state is briefly empty. */
  const resolvedModelId = React.useMemo(() => {
    const trimmed = selectedModel.trim()
    if (trimmed) return trimmed
    return toModelId(agent?.llm_provider, agent?.llm_model)
  }, [selectedModel, agent?.llm_provider, agent?.llm_model])

  React.useEffect(() => {
    if (!open) return
    setToolIcons(loadToolIconsMap())
    const loadToolsAndTags = async () => {
      try {
        setToolsLoading(true)
        const [actionsResult, tagsResult] = await Promise.allSettled([
          toolsService.listActions({ enabled: true, includeGenerated: true }),
          tagService.getTags({ orderBy: 'name', order: 'asc' }),
        ])

        if (actionsResult.status === 'fulfilled') {
          setTools(actionsResult.value)
        } else {
          setTools([])
          log.error('AgentForm load tools failed', actionsResult.reason, { component: 'AgentForm' })
          setErrorMessage(t('agent_form.errors.load_tools'))
        }

        if (tagsResult.status === 'fulfilled') {
          setAvailableTags(tagsResult.value || [])
        } else {
          setAvailableTags([])
          log.warn('AgentForm load tags failed', { component: 'AgentForm' })
        }
      } catch (error) {
        log.error('AgentForm loadToolsAndTags fatal failed', error, { component: 'AgentForm' })
        setErrorMessage(t('agent_form.errors.load_tools'))
      } finally {
        setToolsLoading(false)
      }
    }
    void loadToolsAndTags()
  }, [open, t])

  React.useEffect(() => {
    if (!open) return
    setErrorMessage(null)
    setToolQuery('')
    setToolsOpen(true)
    setDocumentsOpen(true)
    setPromptEditorOpen(false)
    setPromptEditorMode('markdown')
    const storedVisual = loadAgentVisualsMap()[normalizeId(agent?.id)] || {}
    setVisual(storedVisual)
    setCoverPrompt('')
    setCoverStyle('cinematic')
    setCoverError(null)
    setSelectedModel(toModelId(agent?.llm_provider, agent?.llm_model))
    const mergedPrompt = mergePromptParts(agent?.base_prompt || DEFAULT_BASE_PROMPT, agent?.custom_instructions || '')
    const initialDocuments = agent?.documents || []
    const initialDocumentIds = initialDocuments.length > 0
      ? initialDocuments.map((document) => document.id)
      : (agent?.document_ids || [])
    const initialDocumentMap: Record<number, AgentDocument> = {}
    initialDocuments.forEach((document) => {
      initialDocumentMap[document.id] = document
    })
    setDocumentCatalog(initialDocumentMap)
    setFormData({
      name: agent?.name || '',
      display_name: agent?.display_name || '',
      description: agent?.description || '',
      is_enabled: agent?.is_enabled ?? true,
      timezone: agent?.timezone || DEFAULT_AGENT_TIMEZONE,
      base_prompt: mergedPrompt || DEFAULT_BASE_PROMPT,
      custom_instructions: '',
      tool_ids: agent?.tools?.map((tool) => tool.id) || initialToolIds,
      document_ids: initialDocumentIds,
      audience: agent?.audience ?? 'internal',
    })
  }, [agent, initialToolIds, open])

  React.useEffect(() => {
    if (!open) return
    let alive = true
    const initial: Record<number, DocumentPermission> = {}
    ;(agent?.document_ids || agent?.documents?.map((document) => document.id) || []).forEach((id) => {
      initial[id] = 'leer'
    })
    setPermissions(initial)
    setSavedPermissions(initial)
    if (agent?.id) {
      agentsService
        .getAgentDocuments(agent.id)
        .then((response) => {
          if (!alive) return
          const loaded: Record<number, DocumentPermission> = {}
          response.documents.forEach((document) => {
            loaded[document.id] = document.permiso
          })
          setPermissions(loaded)
          setSavedPermissions(loaded)
          setFormData((prev) => ({ ...prev, document_ids: Object.keys(loaded).map(Number) }))
        })
        .catch((error) => log.error('AgentForm load permissions failed', error, { component: 'AgentForm' }))
    }
    return () => {
      alive = false
    }
  }, [agent, open])

  React.useEffect(() => {
    if (!open) return
    let alive = true
    const none = { abilityIds: [], skillIds: [] }
    setCapabilities(none)
    setSavedCapabilities(none)
    const agentId = agent?.id
    if (agentId) {
      Promise.all([agentsService.getAgentAbilities(agentId), agentsService.listSkills()])
        .then(([abilities, skills]) => {
          if (!alive) return
          const loaded = {
            abilityIds: abilities.map((ability) => ability.id),
            skillIds: skills.filter((skill) => skill.agent_ids.includes(agentId)).map((skill) => skill.id),
          }
          setCapabilities(loaded)
          setSavedCapabilities(loaded)
        })
        .catch((error) => log.error('AgentForm load capabilities failed', error, { component: 'AgentForm' }))
    }
    return () => {
      alive = false
    }
  }, [agent, open])

  const permissionChanges = React.useMemo(() => {
    const ids = new Set([...Object.keys(permissions), ...Object.keys(savedPermissions)].map(Number))
    return Array.from(ids).filter((id) => permissions[id] !== savedPermissions[id]).length
  }, [permissions, savedPermissions])

  React.useEffect(() => {
    if (!open) return
    const loadAvailableModels = async () => {
      try {
        setLoadingAvailableModels(true)
        setAvailableModelsError(null)
        const response: AgentAvailableModelsResponse = await agentsService.listAvailableModels()
        setAvailableModelOptions(response.items || [])
        setMediaProcessingModel(response.media_processing_model || null)
      } catch (error) {
        setAvailableModelOptions([])
        setMediaProcessingModel(null)
        setAvailableModelsError(toErrorMessage(error))
      } finally {
        setLoadingAvailableModels(false)
      }
    }
    void loadAvailableModels()
  }, [open])

  const hasGeminiCredential = availableModelOptions.some((model) => model.provider === 'google')

  /** New agent: default to first catalog model once options load. Edit: keep saved id even if not listed. */
  React.useEffect(() => {
    if (!open) return
    if (isEditMode) return
    if (availableModelOptions.length === 0) return
    if (selectedModel.trim()) return
    const first = getFirstCatalogModelId(availableModelOptions)
    if (first) setSelectedModel(first)
  }, [availableModelOptions, open, isEditMode, selectedModel])

  const handleAgentFormModelChange = React.useCallback((modelId: string) => {
    setSelectedModel(modelId)
  }, [])

  const handleGenerateCover = async () => {
    if (!coverPrompt.trim()) return
    try {
      setCoverGenerating(true)
      setCoverError(null)
      const response = await agentsService.generateAgentCover(coverPrompt.trim(), coverStyle)
      setVisual((prev) => ({ ...prev, coverUrl: response.image_url, icon: undefined }))
    } catch (error) {
      setCoverError(toErrorMessage(error))
    } finally {
      setCoverGenerating(false)
    }
  }

  const filteredTools = React.useMemo(() => {
    if (!toolQuery.trim()) return tools
    const query = toolQuery.trim().toLowerCase()
    return tools.filter((tool) => {
      return (
        tool.display_name.toLowerCase().includes(query) ||
        tool.name.toLowerCase().includes(query) ||
        (tool.description || '').toLowerCase().includes(query)
      )
    })
  }, [toolQuery, tools])

  const herramientasDelNegocio = React.useMemo(
    () => filteredTools.filter((tool) => getToolVisibility(tool) !== 'internal'),
    [filteredTools]
  )
  // Las que operan la plataforma. Van en su propia seccion: mezcladas entre las
  // del negocio estaban ahi y no se veian, y ademas no las puede llevar
  // cualquiera.
  const herramientasInternas = React.useMemo(
    () => filteredTools.filter((tool) => getToolVisibility(tool) === 'internal'),
    [filteredTools]
  )

  const [grupoVisible, setGrupoVisible] = React.useState<'business' | 'internal'>('business')

  const filteredToolIds = React.useMemo(() => filteredTools.map((tool) => tool.id), [filteredTools])
  const areAllFilteredToolsSelected = React.useMemo(
    () => filteredToolIds.length > 0 && filteredToolIds.every((toolId) => formData.tool_ids.includes(toolId)),
    [filteredToolIds, formData.tool_ids]
  )

  const publicRiskyToolsCount = React.useMemo(
    () =>
      formData.audience === 'public'
        ? tools.filter((tool) => formData.tool_ids.includes(tool.id) && getToolVisibility(tool) !== 'external').length
        : 0,
    [formData.audience, formData.tool_ids, tools]
  )
  const selectedTools = React.useMemo(() => {
    return tools.filter((tool) => formData.tool_ids.includes(tool.id))
  }, [formData.tool_ids, tools])
  const selectedPromptDocuments = React.useMemo(() => {
    return formData.document_ids
      .map((documentId) => documentCatalog[documentId])
      .filter((document): document is AgentDocument => Boolean(document))
  }, [documentCatalog, formData.document_ids])
  const selectedDocumentsPreview = React.useMemo(() => {
    return selectedPromptDocuments.slice(0, 5)
  }, [selectedPromptDocuments])

  const selectedToolDynamicKeys = React.useMemo(() => {
    return buildSelectedToolDynamicKeys(selectedTools)
  }, [selectedTools])

  const duplicateToolDynamicKeys = React.useMemo(() => {
    return findDuplicateSelectedToolDynamicKeys(selectedTools)
  }, [selectedTools])

  const duplicateToolDynamicTokens = React.useMemo(() => {
    return duplicateToolDynamicKeys.map((key) => `{{tool.${key}}}`)
  }, [duplicateToolDynamicKeys])

  const duplicateToolDynamicKeySet = React.useMemo(() => {
    return new Set(duplicateToolDynamicKeys)
  }, [duplicateToolDynamicKeys])

  const unknownReferencedTokens = React.useMemo(() => {
    return findUnknownReferencedDynamicTokens(
      formData.base_prompt,
      selectedToolDynamicKeys,
      duplicateToolDynamicKeys
    )
  }, [duplicateToolDynamicKeys, formData.base_prompt, selectedToolDynamicKeys])

  const promptValidationMessage = React.useMemo(() => {
    if (unknownReferencedTokens.length > 0) {
      return t('agent_form.errors.invalid_tools_reference', { tools: unknownReferencedTokens.join(', ') })
    }
    if (duplicateToolDynamicTokens.length > 0) {
      return t('agent_form.errors.duplicate_dynamic_variables', {
        tools: duplicateToolDynamicTokens.join(', '),
      })
    }
    return null
  }, [duplicateToolDynamicTokens, t, unknownReferencedTokens])

  const isPromptConfigValid = promptValidationMessage === null

  const filteredToolMentionSuggestions = React.useMemo(() => {
    return buildToolMentionSuggestions(
      selectedTools,
      selectedToolDynamicKeys
    ).filter((item) => !duplicateToolDynamicKeySet.has(item.token.replace(/^tool\./, '')))
  }, [duplicateToolDynamicKeySet, selectedToolDynamicKeys, selectedTools])

  const systemMentionSuggestions = React.useMemo(() => {
    return buildSystemMentionSuggestions((token) =>
      t('agent_form.fields.prompt_alias_description', { token })
    )
  }, [t])

  const variableMentionOptions = React.useMemo(() => {
    return [...systemMentionSuggestions, ...filteredToolMentionSuggestions]
      .map((item) => ({
        token: item.token,
        label: item.label,
        description: item.description,
      }))
      .filter((item, index, all) => all.findIndex((other) => other.token === item.token) === index)
  }, [filteredToolMentionSuggestions, systemMentionSuggestions])

  const updateField = React.useCallback(<K extends keyof AgentFormState>(field: K, value: AgentFormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const updatePromptSelection = React.useCallback((start: number, end: number) => {
    promptSelectionRef.current = start <= end ? { start, end } : { start: end, end: start }
  }, [])

  const handlePromptEditorChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateField('base_prompt', event.target.value)
    },
    [updateField]
  )

  const handleMainPromptChange = React.useCallback(
    (value: string) => {
      updateField('base_prompt', value)
    },
    [updateField]
  )

  const captureMarkdownSelection = React.useCallback(() => {
    const input = promptEditorTextareaRef.current
    if (!input) return
    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? input.value.length
    updatePromptSelection(start, end)
  }, [updatePromptSelection])

  const capturePreviewSelection = React.useCallback(() => {
    const root = promptPreviewRef.current
    const selection = window.getSelection?.()
    if (!root || !selection || selection.rangeCount === 0 || selection.isCollapsed) return
    const range = selection.getRangeAt(0)
    if (!root.contains(range.commonAncestorContainer)) return
    promptPreviewSelectionTextRef.current = selection.toString()
  }, [])

  const preserveSelection = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }, [])

  const getSelectionRange = React.useCallback((current: string): PromptSelection => {
    const input = promptEditorTextareaRef.current
    if (input) {
      return {
        start: input.selectionStart ?? current.length,
        end: input.selectionEnd ?? current.length,
      }
    }
    const selectedText = (window.getSelection?.()?.toString() || promptPreviewSelectionTextRef.current).trim()
    if (selectedText) {
      const index = current.indexOf(selectedText)
      if (index !== -1) {
        return { start: index, end: index + selectedText.length }
      }
    }
    if (promptSelectionRef.current) return promptSelectionRef.current
    return { start: current.length, end: current.length }
  }, [])

  const applyInlineFormat = React.useCallback(
    (prefix: string, suffix: string = prefix, placeholder = 'texto') => {
      const current = formData.base_prompt
      const { start, end } = getSelectionRange(current)
      const selectedText = current.slice(start, end)
      const hasSelection = start !== end
      const wrappedAroundSelection =
        start >= prefix.length &&
        current.slice(start - prefix.length, start) === prefix &&
        current.slice(end, end + suffix.length) === suffix
      const selectedAlreadyWrapped =
        selectedText.startsWith(prefix) &&
        selectedText.endsWith(suffix) &&
        selectedText.length >= prefix.length + suffix.length

      let next = current
      let nextStart = start
      let nextEnd = end

      if (hasSelection && wrappedAroundSelection) {
        next = `${current.slice(0, start - prefix.length)}${selectedText}${current.slice(end + suffix.length)}`
        nextStart = start - prefix.length
        nextEnd = end - prefix.length
      } else if (hasSelection && selectedAlreadyWrapped) {
        const content = selectedText.slice(prefix.length, selectedText.length - suffix.length)
        next = `${current.slice(0, start)}${content}${current.slice(end)}`
        nextStart = start
        nextEnd = start + content.length
      } else {
        const content = selectedText || placeholder
        next = `${current.slice(0, start)}${prefix}${content}${suffix}${current.slice(end)}`
        if (hasSelection) {
          nextStart = start + prefix.length
          nextEnd = start + prefix.length + selectedText.length
        } else {
          const cursor = start + prefix.length + placeholder.length
          nextStart = cursor
          nextEnd = cursor
        }
      }

      updateField('base_prompt', next)
      updatePromptSelection(nextStart, nextEnd)
      setPromptEditorMode('markdown')

      requestAnimationFrame(() => {
        const input = promptEditorTextareaRef.current
        if (!input) return
        input.focus()
        input.setSelectionRange(nextStart, nextEnd)
      })
    },
    [formData.base_prompt, getSelectionRange, updateField, updatePromptSelection]
  )

  const applyLinePrefixFormat = React.useCallback(
    (prefix: string) => {
      const current = formData.base_prompt
      const { start: rawStart, end: rawEnd } = getSelectionRange(current)
      const hasSelection = rawStart !== rawEnd
      const start = hasSelection ? rawStart : current.lastIndexOf('\n', rawStart - 1) + 1
      const nextNewline = current.indexOf('\n', rawStart)
      const end = hasSelection ? rawEnd : (nextNewline === -1 ? current.length : nextNewline)
      const selectedText = current.slice(start, end)
      const lines = (selectedText || 'texto').split('\n')
      const canToggleOff = lines.every((line) => line.startsWith(prefix))
      const replacement = lines.map((line) => (canToggleOff ? line.slice(prefix.length) : `${prefix}${line}`)).join('\n')
      const next = `${current.slice(0, start)}${replacement}${current.slice(end)}`

      updateField('base_prompt', next)
      updatePromptSelection(start, start + replacement.length)
      setPromptEditorMode('markdown')

      requestAnimationFrame(() => {
        const input = promptEditorTextareaRef.current
        if (!input) return
        input.focus()
        input.setSelectionRange(start, start + replacement.length)
      })
    },
    [formData.base_prompt, getSelectionRange, updateField, updatePromptSelection]
  )

  React.useEffect(() => {
    if (!promptEditorOpen || promptEditorMode !== 'markdown') return
    const selection = promptSelectionRef.current
    if (!selection) return
    requestAnimationFrame(() => {
      const input = promptEditorTextareaRef.current
      if (!input) return
      input.focus()
      input.setSelectionRange(selection.start, selection.end)
    })
  }, [promptEditorMode, promptEditorOpen])

  /** Una herramienta de la lista. Se dibuja igual en las dos secciones. */
  const renderizarHerramienta = (tool: MCPAction) => {
    const checked = formData.tool_ids.includes(tool.id)
    const visibility = getToolVisibility(tool)
    const bloqueada = herramientaBloqueadaPorAudiencia(formData.audience, visibility, checked)
    return (
        <Label
          key={tool.id}
          title={bloqueada ? t('agent_form.sections.tools_group_internal_blocked') : undefined}
          className={cn(
            'flex items-start gap-2 rounded-lg border border-transparent px-3 py-2',
            bloqueada
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer hover:border-border hover:bg-muted/40'
          )}
        >
          <Checkbox
            checked={checked}
            onCheckedChange={(valor) => toggleTool(tool.id, valor === true)}
            disabled={loading || bloqueada}
            className="mt-1"
          />
          <div className="min-w-0 flex items-start gap-2">
            <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border bg-muted/40">
              {toolIcons[tool.id]?.icon ? (
                <AgentVisualIcon
                  value={toolIcons[tool.id]?.icon}
                  colorClass={toolIcons[tool.id]?.colorClass}
                  className="h-3.5 w-3.5"
                />
              ) : (
                <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium">{tool.display_name}</p>
              <div className="mt-1">
                <Badge
                  variant="outline"
                  className={cn(
                    'h-5 px-1.5 text-[10px]',
                    visibility === 'internal' && 'border-warning/30 text-warning',
                    visibility === 'mixed' && 'border-primary/30 text-primary',
                    visibility === 'external' && 'border-success/30 text-success'
                  )}
                >
                  {visibility === 'internal'
                    ? t('agent_form.sections.visibility_internal')
                    : visibility === 'mixed'
                      ? t('agent_form.sections.visibility_mixed')
                      : t('agent_form.sections.visibility_external')}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{tool.description || tool.name}</p>
            </div>
          </div>
        </Label>
      )
  }

  const toggleTool = (toolId: string, checked: boolean) => {
    const next = new Set(formData.tool_ids)
    if (checked) next.add(toolId)
    else next.delete(toolId)
    updateField('tool_ids', Array.from(next))
  }

  const toggleSelectAllFilteredTools = () => {
    const next = new Set(formData.tool_ids)
    if (areAllFilteredToolsSelected) {
      filteredToolIds.forEach((toolId) => next.delete(toolId))
    } else {
      filteredToolIds.forEach((toolId) => next.add(toolId))
    }
    updateField('tool_ids', Array.from(next))
  }

  const handleDocumentsChange = (documentIds: number[]) => {
    updateField('document_ids', documentIds)
  }

  const handleDocumentCatalogUpdate = React.useCallback((items: AgentAvailableDocument[]) => {
    setDocumentCatalog((prev) => {
      const next = { ...prev }
      for (const item of items) {
        next[item.id] = {
          id: item.id,
          title: item.title,
          status: item.status,
          document_type: item.document_type,
        }
      }
      return next
    })
  }, [])

  const validateForm = (): string | null => {
    if (!isEditMode && !formData.name.trim()) return t('agent_form.errors.required_internal_name')
    if (!formData.display_name.trim()) return t('agent_form.errors.required_name')
    if (!formData.timezone.trim()) return t('agent_form.errors.required_timezone')
    if (!formData.base_prompt.trim()) return t('agent_form.errors.required_base_prompt')
    if (availableModelOptions.length === 0) return t('agent_form.errors.no_models_available')
    if (!parseModelId(resolvedModelId)) return t('agent_form.errors.invalid_model')
    if (!isModelIdInCatalog(resolvedModelId, availableModelOptions)) {
      return t('agent_form.errors.model_not_in_catalog')
    }
    if (promptValidationMessage) return promptValidationMessage
    return null
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setErrorMessage(null)

    const validationError = validateForm()
    if (validationError) {
      setErrorMessage(validationError)
      return
    }

    try {
      setLoading(true)
      const parsedModel = parseModelId(resolvedModelId)
      if (!parsedModel) {
        setErrorMessage(t('agent_form.errors.invalid_model'))
        setLoading(false)
        return
      }
      const reasoningPayload = {
        openai_reasoning_effort: openaiReasoningEffortForApiPayload(parsedModel.provider),
      }
      let savedAgentId = agent?.id || ''
      if (agent) {
        const payload: AgentUpdatePayload = {
          name: agent.name,
          display_name: formData.display_name.trim(),
          description: formData.description.trim(),
          is_enabled: formData.is_enabled,
          timezone: formData.timezone.trim(),
          base_prompt: formData.base_prompt.trim(),
          custom_instructions: '',
          tool_ids: formData.tool_ids,
          audience: formData.audience,
          llm_provider: parsedModel.provider,
          llm_model: parsedModel.model,
          ...reasoningPayload,
          fallback_vision_model: 'gemini-2.5-pro',
          fallback_video_model: 'gemini-2.5-pro',
          fallback_pdf_model: 'gemini-2.5-pro',
        }
        const updated = await agentsService.updateAgent(agent.id, payload)
        savedAgentId = updated?.id || agent.id
      } else {
        const payload: AgentCreatePayload = {
          name: formData.name.trim(),
          display_name: formData.display_name.trim(),
          description: formData.description.trim(),
          is_enabled: formData.is_enabled,
          timezone: formData.timezone.trim(),
          prompt_mode: 'isolated',
          base_prompt: formData.base_prompt.trim(),
          custom_instructions: '',
          tool_ids: formData.tool_ids,
          audience: formData.audience,
          llm_provider: parsedModel.provider,
          llm_model: parsedModel.model,
          ...reasoningPayload,
          fallback_vision_model: 'gemini-2.5-pro',
          fallback_video_model: 'gemini-2.5-pro',
          fallback_pdf_model: 'gemini-2.5-pro',
        }
        const created = await agentsService.createAgent(payload)
        savedAgentId = created?.id || ''
      }
      // Documents and their permissions go in the call made for that, which
      // replaces the set whole (POST /agents/{id}/documents/).
      if (savedAgentId && (!agent || permissionChanges > 0)) {
        await agentsService.assignDocuments(savedAgentId, Object.keys(permissions).map(Number), permissions)
        setSavedPermissions(permissions)
      }
      if (savedAgentId) {
        await saveCapabilities(savedAgentId, capabilities, savedCapabilities)
        setSavedCapabilities(capabilities)
      }
      if (savedAgentId) {
        saveAgentVisual(savedAgentId, visual)
      }

      onSuccess?.()
      if (agent) {
        toast.success(t('agent_form.notifications.saved_changes'))
      } else {
        onOpenChange(false)
      }
    } catch (error) {
      log.error('AgentForm submit failed', error, { component: 'AgentForm', agentId: agent?.id })
      setErrorMessage(toErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent side="right" className="border-border p-0 sm:w-[60vw] sm:rounded-none">
        <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
          <DialogHeader className="border-b border-border bg-card/70 px-4 py-3 sm:px-6 sm:py-4">
            <DialogTitle className="text-lg font-semibold">
              {agent ? t('agent_form.dialog_edit_title') : t('agent_form.dialog_create_title')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('agent_form.dialog_sr_desc')}
            </DialogDescription>
          </DialogHeader>

          {errorMessage && (
            <div className="px-6 pt-4">
              <Alert variant="destructive">
                <AlertTitle>{t('agent_form.error_title')}</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            </div>
          )}

          <div className="min-h-0 flex-1">
            <ScrollArea className="h-full">
              <div className="grid gap-4 p-6 lg:grid-cols-[1.2fr_0.8fr]">
                <section className="space-y-4">
                  <AgentVisualEditor
                    visual={visual}
                    onVisualChange={setVisual}
                    prompt={coverPrompt}
                    onPromptChange={setCoverPrompt}
                    style={coverStyle}
                    onStyleChange={setCoverStyle}
                    generating={coverGenerating}
                    generateError={coverError}
                    onGenerate={() => void handleGenerateCover()}
                  />

                  <div className="rounded-lg border bg-card/50 p-4">
                    {isEditMode ? (
                      <div className="space-y-2">
                        <Label htmlFor="agent-display-name">{t('agent_form.fields.name_edit_label')}</Label>
                        <Input
                          id="agent-display-name"
                          value={formData.display_name}
                          onChange={(e) => updateField('display_name', e.target.value)}
                          placeholder={t('agent_form.fields.display_name_placeholder')}
                          disabled={loading}
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="agent-name">{t('agent_form.fields.internal_name_label')}</Label>
                          <Input
                            id="agent-name"
                            value={formData.name}
                            onChange={(e) => updateField('name', e.target.value)}
                            placeholder={t('agent_form.fields.internal_name_placeholder')}
                            disabled={loading}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="agent-display-name">{t('agent_form.fields.visible_name_label')}</Label>
                          <Input
                            id="agent-display-name"
                            value={formData.display_name}
                            onChange={(e) => updateField('display_name', e.target.value)}
                            placeholder={t('agent_form.fields.display_name_placeholder')}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    )}

                    <div className="mt-4 space-y-2">
                      <Label htmlFor="agent-description">{t('agent_form.fields.description_label')}</Label>
                      <Textarea
                        id="agent-description"
                        value={formData.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        placeholder={t('agent_form.fields.description_placeholder')}
                        rows={3}
                        disabled={loading}
                      />
                    </div>

                    <div className="mt-4 space-y-2">
                      <Label className="text-sm">{t('agent_form.fields.model_label')}</Label>
                      <Select
                        value={
                          isModelIdInCatalog(resolvedModelId, availableModelOptions)
                            ? resolvedModelId
                            : undefined
                        }
                        onValueChange={handleAgentFormModelChange}
                        disabled={loading || loadingAvailableModels || availableModelOptions.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              loadingAvailableModels
                                ? t('agent_form.fields.model_loading_options')
                                : t('agent_form.fields.model_missing_provider_keys')
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {availableModelOptions.map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {resolvedModelId.trim() &&
                        !loadingAvailableModels &&
                        availableModelOptions.length > 0 &&
                        !isModelIdInCatalog(resolvedModelId, availableModelOptions) && (
                          <Alert className="border-warning/30 bg-warning/10">
                            <AlertTitle>{t('agent_form.fields.model_unavailable_title')}</AlertTitle>
                            <AlertDescription>
                              {t('agent_form.fields.model_unavailable_body', { model: resolvedModelId })}
                            </AlertDescription>
                          </Alert>
                        )}
                      <p className="text-xs text-muted-foreground">
                        {t('agent_form.fields.models_by_configured_providers')}
                      </p>
                      {availableModelsError && (
                        <p className="text-xs text-destructive">
                          {t('agent_form.errors.provider_status_error', { error: availableModelsError })}
                        </p>
                      )}
                    </div>

                    <div className="mt-4 space-y-2">
                      <Label className="text-sm">{t('agent_form.fields.media_model_label')}</Label>
                      <Select value={mediaProcessingModel?.id} disabled>
                        <SelectTrigger>
                          <SelectValue placeholder={t('agent_form.fields.model_loading_options')} />
                        </SelectTrigger>
                        <SelectContent>
                          {mediaProcessingModel && (
                            <SelectItem value={mediaProcessingModel.id}>
                              {mediaProcessingModel.label}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {t('agent_form.fields.media_model_help')}
                      </p>
                      {!loadingAvailableModels && !hasGeminiCredential && (
                        <Alert className="border-warning/30 bg-warning/10">
                          <AlertTitle>{t('agent_form.fields.missing_gemini_key_title')}</AlertTitle>
                          <AlertDescription>
                            {t('agent_form.fields.missing_gemini_key_body')}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>

                    {/* <div className="mt-4 space-y-2">
                      <Label className="text-sm">{t('agent_form.fields.audience_label')}</Label>
                      <Select value={formData.audience} onValueChange={(value: AgentAudience) => updateField('audience', value)} disabled={loading}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="internal">{t('agent_form.fields.audience_internal')}</SelectItem>
                          <SelectItem value="public">{t('agent_form.fields.audience_public')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div> */}

                    <div className="mt-4 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium">{t('agent_form.fields.status_label')}</p>
                        <p className="text-xs text-muted-foreground">
                          {formData.is_enabled ? t('agent_form.fields.status_enabled') : t('agent_form.fields.status_disabled')}
                        </p>
                      </div>
                      <Switch
                        id="agent-enabled"
                        checked={formData.is_enabled}
                        onCheckedChange={(checked) => updateField('is_enabled', checked)}
                        disabled={loading}
                      />
                    </div>

                    <div className="mt-4 space-y-2">
                      <AgentTimezonePicker
                        id="agent-timezone"
                        value={formData.timezone}
                        onChange={(timezone) => updateField('timezone', timezone)}
                        label={t('agent_form.fields.timezone_label')}
                        helpText={t('agent_form.fields.timezone_help')}
                        placeholder={t('agent_form.fields.timezone_placeholder')}
                        recommendedLabel={t('agent_form.fields.timezone_recommended')}
                        allLabel={t('agent_form.fields.timezone_all')}
                        currentTimeLabel={t('agent_form.fields.timezone_current_time')}
                        defaultBadgeLabel={t('agent_form.fields.timezone_default_badge')}
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border bg-card/50 p-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <Label htmlFor="agent-base-prompt">{t('agent_form.fields.prompt_label')}</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPromptEditorOpen(true)}
                          >
                            {t('agent_form.buttons.edit_in_popup')}
                          </Button>
                        </div>
                        <PromptRichEditor
                          id="agent-base-prompt"
                          ariaLabel={t('agent_form.fields.prompt_label')}
                          value={formData.base_prompt}
                          onChange={handleMainPromptChange}
                          disabled={loading}
                          placeholder={t('agent_form.fields.prompt_placeholder')}
                          tools={selectedTools.map((tool) => ({
                            id: tool.id,
                            name: tool.name,
                            displayName: tool.display_name,
                          }))}
                          tags={availableTags.map((tag) => ({
                            id: String(tag.id),
                            name: tag.name,
                            color: tag.color,
                            description: tag.description,
                          }))}
                          documents={selectedPromptDocuments.map((document) => ({
                            id: String(document.id),
                            title: document.title,
                            documentType: document.document_type,
                          }))}
                          variableOptions={variableMentionOptions}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('agent_form.fields.prompt_help')} <code>{'{{system.name}}'}</code>, <code>{'{{system.conversation_id}}'}</code>, <code>{'{{tool.order_id}}'}</code>, <code>@tool_name</code>, <code>@tag:123</code>, <code>@doc:101</code>.
                        </p>
                        {unknownReferencedTokens.length > 0 && (
                          <Alert className="border-warning/30 bg-warning/10">
                            <AlertTitle>{t('agent_form.fields.prompt_invalid_tools_title')}</AlertTitle>
                            <AlertDescription>
                              {t('agent_form.fields.prompt_invalid_tools_body', { tools: unknownReferencedTokens.join(', ') })}
                            </AlertDescription>
                          </Alert>
                        )}
                        {duplicateToolDynamicTokens.length > 0 && (
                          <Alert className="border-destructive/30 bg-destructive/10">
                            <AlertTitle>{t('agent_form.fields.prompt_duplicate_variables_title')}</AlertTitle>
                            <AlertDescription>
                              {t('agent_form.fields.prompt_duplicate_variables_body', {
                                tools: duplicateToolDynamicTokens.join(', '),
                              })}
                            </AlertDescription>
                          </Alert>
                        )}
                        <div className="mt-4">
                          <p className="text-xs font-medium">{t('agent_form.sections.tool_reference_title')}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {(selectedTools.length > 0 ? selectedTools : tools.slice(0, 12)).map((tool) => (
                              <Badge key={tool.id} variant="secondary" className="max-w-full truncate text-[10px]">
                                {tool.display_name}
                                <span className="ml-1 text-muted-foreground">(@{tool.name})</span>
                              </Badge>
                            ))}
                            {selectedTools.length === 0 && tools.length > 12 && (
                              <Badge variant="outline" className="text-[10px]">
                                +{tools.length - 12} {t('agent_form.generic.more')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Guardrails temporarily paused while behavior prompt is consolidated into one single field. */}
                      {/* <AgentGuardrailsEditor value={guardrails} onChange={setGuardrails} disabled={loading} /> */}
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  <SeccionPlegable
                    plana
                    abierta={toolsOpen}
                    onAlternar={() => setToolsOpen((prev) => !prev)}
                    icono={<Wrench className="h-4 w-4 shrink-0 text-primary" />}
                    titulo={t('agent_form.sections.tools_title')}
                    estado={<Badge variant="secondary">{t('agent_form.sections.tools_selected_count', { count: formData.tool_ids.length })}</Badge>}
                  >

                        <div className="mt-3 relative">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            value={toolQuery}
                            onChange={(event) => setToolQuery(event.target.value)}
                            placeholder={t('agent_form.sections.search_tool_placeholder')}
                            className="pl-9"
                            disabled={loading || toolsLoading}
                          />
                        </div>

                        <div className="mt-2 flex justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={toggleSelectAllFilteredTools}
                            disabled={loading || toolsLoading || filteredToolIds.length === 0}
                          >
                            {areAllFilteredToolsSelected ? 'Clear all' : 'Add all'}
                          </Button>
                        </div>

                        <Separator className="my-4" />

                        <Tabs value={grupoVisible} onValueChange={(value) => setGrupoVisible(value as typeof grupoVisible)}>
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="business">
                              {t('agent_form.sections.tools_group_business')} ({herramientasDelNegocio.length})
                            </TabsTrigger>
                            <TabsTrigger value="internal">
                              {t('agent_form.sections.tools_group_internal_short')} ({herramientasInternas.length})
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>

                        <div className="mt-4">
                          <ScrollArea className="h-[32vh] p-2">
                            {formData.audience === 'public' && publicRiskyToolsCount > 0 && (
                              <Alert className="mb-3 border-warning/30 bg-warning/10">
                                <AlertTitle>{t('agent_form.sections.tools_visibility_warning_title')}</AlertTitle>
                                <AlertDescription>
                                  {t('agent_form.sections.tools_visibility_warning_body', { count: publicRiskyToolsCount })}
                                </AlertDescription>
                              </Alert>
                            )}
                            {toolsLoading ? (
                              <div className="space-y-2 p-3">
                                {Array.from({ length: 4 }).map((_, index) => (
                                  <div key={`agent-form-tool-skeleton-${index}`} className="flex items-start gap-2 rounded-lg border p-2">
                                    <Skeleton className="mt-1 h-4 w-4 rounded-md" />
                                    <div className="min-w-0 flex-1 space-y-2">
                                      <Skeleton className="h-4 w-40" />
                                      <Skeleton className="h-3 w-full" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : filteredTools.length === 0 ? (
                              <div className="p-3 text-sm text-muted-foreground">{t('agent_form.sections.no_tools_filtered')}</div>
                            ) : (
                              <div className="space-y-2">
                                {grupoVisible === 'business' ? (
                                  herramientasDelNegocio.length > 0 ? (
                                    herramientasDelNegocio.map(renderizarHerramienta)
                                  ) : (
                                    <p className="p-3 text-sm text-muted-foreground">
                                      {t('agent_form.sections.no_tools_filtered')}
                                    </p>
                                  )
                                ) : herramientasInternas.length > 0 ? (
                                  <>
                                    <p className="px-1 pb-1 text-xs text-muted-foreground">
                                      {formData.audience === 'public'
                                        ? t('agent_form.sections.tools_group_internal_blocked')
                                        : t('agent_form.sections.tools_group_internal_help')}
                                    </p>
                                    {herramientasInternas.map(renderizarHerramienta)}
                                  </>
                                ) : (
                                  <p className="p-3 text-sm text-muted-foreground">
                                    {t('agent_form.sections.no_tools_filtered')}
                                  </p>
                                )}
                              </div>
                            )}
                          </ScrollArea>
                        </div>
                  </SeccionPlegable>

                  <SeccionPlegable
                    plana
                    abierta={documentsOpen}
                    onAlternar={() => setDocumentsOpen((prev) => !prev)}
                    icono={<FileText className="h-4 w-4 shrink-0 text-primary" />}
                    titulo={t('agent_form.permisos.titulo')}
                    estado={<Badge variant="secondary">{t('agent_form.sections.documents_selected_count', { count: formData.document_ids.length })}</Badge>}
                  >
                    <AgentDocumentsSelector
                      selectedDocumentIds={formData.document_ids}
                      onChange={handleDocumentsChange}
                      onCatalogUpdate={handleDocumentCatalogUpdate}
                      disabled={loading}
                      listHeightClass="h-[32vh]"
                      permissions={permissions}
                      onPermissionsChange={setPermissions}
                    />
                  </SeccionPlegable>
                  {!documentsOpen && selectedDocumentsPreview.length > 0 && (
                    <div className="flex flex-wrap gap-2 px-4">
                      {selectedDocumentsPreview.map((document) => (
                        <Badge key={document.id} variant="outline" className="max-w-full truncate text-[10px]">
                          {document.title}
                        </Badge>
                      ))}
                      {formData.document_ids.length > selectedDocumentsPreview.length && (
                        <Badge variant="outline" className="text-[10px]">
                          +{formData.document_ids.length - selectedDocumentsPreview.length} {t('agent_form.generic.more')}
                        </Badge>
                      )}
                    </div>
                  )}

                  <SeccionPlegable
                    plana
                    abierta={capabilitiesOpen}
                    onAlternar={() => setCapabilitiesOpen((prev) => !prev)}
                    icono={<Sparkles className="h-4 w-4 shrink-0 text-primary" />}
                    titulo={t('agent_form.capacidades.titulo')}
                    estado={
                      <Badge variant="secondary">
                        {t('agent_form.capacidades.resumen', {
                          abilities: capabilities.abilityIds.length,
                          skills: capabilities.skillIds.length,
                        })}
                      </Badge>
                    }
                  >
                    <AgentCapabilities
                      abilityIds={capabilities.abilityIds}
                      skillIds={capabilities.skillIds}
                      onAbilityIdsChange={(abilityIds) => setCapabilities((prev) => ({ ...prev, abilityIds }))}
                      onSkillIdsChange={(skillIds) => setCapabilities((prev) => ({ ...prev, skillIds }))}
                      disabled={loading}
                    />
                  </SeccionPlegable>
                </section>
              </div>
            </ScrollArea>
          </div>

          <Dialog open={promptEditorOpen} onOpenChange={setPromptEditorOpen}>
            <DialogContent className="max-h-[88vh] w-[94vw] max-w-5xl overflow-hidden p-0">
              <DialogHeader className="border-b border-border bg-card/70 px-5 py-4">
                <DialogTitle className="text-lg">{t('agent_form.prompt_editor.title')}</DialogTitle>
                <DialogDescription className="sr-only">
                  {t('agent_form.prompt_editor.sr_desc')}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2 px-5 py-4">
                <Tabs value={promptEditorMode} onValueChange={(value) => setPromptEditorMode(value as 'markdown' | 'preview')} className="rounded-lg border bg-background/60 p-3">
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <Button type="button" size="icon" variant="ghost" aria-label={t('agent_form.prompt_editor.aria.bold')} onMouseDown={preserveSelection} onClick={() => applyInlineFormat('**')} disabled={loading}>
                      <Bold className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={t('agent_form.prompt_editor.aria.italic')} onMouseDown={preserveSelection} onClick={() => applyInlineFormat('*')} disabled={loading}>
                      <Italic className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={t('agent_form.prompt_editor.aria.heading')} onMouseDown={preserveSelection} onClick={() => applyLinePrefixFormat('# ')} disabled={loading}>
                      <Heading1 className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={t('agent_form.prompt_editor.aria.bullets')} onMouseDown={preserveSelection} onClick={() => applyLinePrefixFormat('- ')} disabled={loading}>
                      <List className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={t('agent_form.prompt_editor.aria.numbers')} onMouseDown={preserveSelection} onClick={() => applyLinePrefixFormat('1. ')} disabled={loading}>
                      <ListOrdered className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={t('agent_form.prompt_editor.aria.quote')} onMouseDown={preserveSelection} onClick={() => applyLinePrefixFormat('> ')} disabled={loading}>
                      <Quote className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={t('agent_form.prompt_editor.aria.code')} onMouseDown={preserveSelection} onClick={() => applyInlineFormat('`')} disabled={loading}>
                      <Code className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={t('agent_form.prompt_editor.aria.link')} onMouseDown={preserveSelection} onClick={() => applyInlineFormat('[', '](https://)', 'texto')} disabled={loading}>
                      <Link2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="markdown">{t('agent_form.prompt_editor.tab_markdown')}</TabsTrigger>
                    <TabsTrigger value="preview">{t('agent_form.prompt_editor.tab_preview')}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="markdown" className="mt-3">
                    <div className="relative">
                      <Textarea
                        ref={promptEditorTextareaRef}
                        value={formData.base_prompt}
                        onChange={handlePromptEditorChange}
                        placeholder={t('agent_form.prompt_editor.placeholder')}
                        rows={18}
                        disabled={loading}
                        className="min-h-[48vh] border-0 focus-visible:ring-0"
                        onSelect={captureMarkdownSelection}
                        onKeyUp={captureMarkdownSelection}
                        onClick={captureMarkdownSelection}
                        onScroll={captureMarkdownSelection}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="preview" className="mt-3">
                    <div
                      ref={promptPreviewRef}
                      className="min-h-[48vh] max-h-[56vh] overflow-auto rounded-lg bg-muted/30 p-4 text-sm"
                      onMouseUp={capturePreviewSelection}
                      onKeyUp={capturePreviewSelection}
                    >
                      {formData.base_prompt.trim() ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ children }) => <h1 className="mb-3 mt-1 text-2xl font-semibold tracking-tight">{children}</h1>,
                            h2: ({ children }) => <h2 className="mb-2 mt-1 text-lg font-semibold tracking-tight">{children}</h2>,
                            h3: ({ children }) => <h3 className="mb-2 mt-1 text-lg font-semibold tracking-tight">{children}</h3>,
                            h4: ({ children }) => <h4 className="mb-2 mt-1 text-sm font-semibold">{children}</h4>,
                            p: ({ children }) => <p className="mb-2 leading-relaxed last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="mb-2 list-disc pl-5">{children}</ul>,
                            ol: ({ children }) => <ol className="mb-2 list-decimal pl-5">{children}</ol>,
                            li: ({ children }) => <li className="mb-1">{children}</li>,
                            code: ({ children }) => (
                              <code className="rounded-md bg-muted px-1 py-0.5 text-xs">{children}</code>
                            ),
                          }}
                        >
                          {formData.base_prompt}
                        </ReactMarkdown>
                      ) : (
                        <p className="text-xs text-muted-foreground">{t('agent_form.prompt_editor.empty_preview')}</p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              <DialogFooter className="border-t border-border px-5 py-4">
                <Button type="button" onClick={() => setPromptEditorOpen(false)}>
                  {t('agent_form.buttons.done')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {agent && permissionChanges > 0 && (
            // Changing what an agent can consult is sensitive (R9): it is said
            // before saving, and saving is the confirmation (7.3). Not a dialog:
            // this form already is one.
            <p role="status" className="mx-3 rounded-md bg-warning/10 px-3 py-2 text-sm text-warning sm:mx-6">
              {t('agent_form.permisos.cambios', { count: permissionChanges })}
            </p>
          )}
          <DialogFooter className="flex-row min-w-0 items-center gap-2 border-t border-border bg-background/90 px-3 py-2 sm:px-6 sm:py-4">
            <Button variant="outline" size="xs" type="button" className="shrink-0 text-[11px] whitespace-nowrap sm:h-9 sm:px-4 sm:text-sm" onClick={() => onOpenChange(false)} disabled={loading}>
              {t('agent_form.buttons.cancel')}
            </Button>
            <Button type="submit" size="xs" className="ml-auto shrink-0 text-[11px] whitespace-nowrap sm:h-9 sm:px-4 sm:text-sm" disabled={loading || !isPromptConfigValid}>
              {loading
                ? t('agent_form.buttons.saving')
                : agent
                  ? permissionChanges > 0
                    ? t('agent_form.permisos.confirmar_guardar')
                    : t('agent_form.buttons.save_changes')
                  : t('agent_form.buttons.create_agent')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
